import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Search, PhoneCall } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import OwnerSelect from '../components/OwnerSelect';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const emptyCase = {
  caseKey: '',
  title: '',
  callerType: 'cualquiera',
  intentType: 'consulta',
  handlingMode: 'directo',
  allowServiceRead: true,
  allowServiceEdit: false,
  allowTransfer: false,
  allowMessageEmail: false,
  defaultContactId: null,
  instructions: '',
  sortOrder: 100,
  active: true,
  isFallback: false,
  allowedCallersMode: 'cualquiera',
  allowedReceiversMode: 'cualquiera',
  ownerId: null,
};

const CallCases = () => {
  const modal = useModal();
  const [cases, setCases] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('callcases:search', '');
  const [viewMode, setViewMode] = useViewMode('callcases');
  const fl = useFieldLayout('callcases', viewMode);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCase);
  const [allowedCallerIds, setAllowedCallerIds] = useState([]);
  const [allowedReceiverIds, setAllowedReceiverIds] = useState([]);
  const [caseToDelete, setCaseToDelete] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [casesRes, contactsRes] = await Promise.all([
        api.get('/call-cases'),
        api.get('/phonebook'),
      ]);
      setCases(Array.isArray(casesRes.data) ? casesRes.data : []);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
    } catch (e) {
      modal.error('Error cargando casos: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyCase);
    setAllowedCallerIds([]);
    setAllowedReceiverIds([]);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      caseKey: c.caseKey, title: c.title,
      callerType: c.callerType, intentType: c.intentType, handlingMode: c.handlingMode,
      allowServiceRead: c.allowServiceRead, allowServiceEdit: c.allowServiceEdit,
      allowTransfer: c.allowTransfer, allowMessageEmail: c.allowMessageEmail,
      defaultContactId: c.defaultContactId,
      instructions: c.instructions || '',
      sortOrder: c.sortOrder, active: c.active, isFallback: c.isFallback,
      allowedCallersMode: c.allowedCallersMode, allowedReceiversMode: c.allowedReceiversMode,
      ownerId: c.ownerId ?? null,
    });
    setAllowedCallerIds((c.allowedCallers || []).map((x) => x.contactId));
    setAllowedReceiverIds((c.allowedReceivers || []).map((x) => x.contactId));
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.caseKey.trim() || !form.title.trim()) {
      modal.error('caseKey y title son obligatorios');
      return;
    }
    try {
      let saved;
      if (editing) {
        const r = await api.put(`/call-cases/${editing.id}`, form);
        saved = r.data;
      } else {
        const r = await api.post('/call-cases', form);
        saved = r.data;
      }
      // Sincronizar listas M:N
      await Promise.all([
        api.put(`/call-cases/${saved.id}/allowed-callers`, { contactIds: allowedCallerIds }),
        api.put(`/call-cases/${saved.id}/allowed-receivers`, { contactIds: allowedReceiverIds }),
      ]);
      await loadAll();
      closeModal();
    } catch (err) {
      modal.error(err.response?.data?.message || 'Error guardando caso');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/call-cases/${id}`);
      await loadAll();
      setCaseToDelete(null);
    } catch (e) {
      modal.error(e.response?.data?.message || 'Error borrando');
    }
  };

  const toggleId = (list, setList, id) => {
    if (list.includes(id)) setList(list.filter((x) => x !== id));
    else setList([...list, id]);
  };

  const filtered = cases.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.caseKey.toLowerCase().includes(q) || c.title.toLowerCase().includes(q);
  });

  const columns = [
    { key: 'title', header: 'Título', className: 'dt-strong', fixed: true, render: (c) => (
      <div>
        {c.title}
        <div className="dt-sub" style={{ fontFamily: 'monospace' }}>{c.caseKey}</div>
      </div>
    ) },
    { key: 'allowTransfer', header: 'Transferencia', render: (c) => (c.allowTransfer ? 'permitida' : 'no') },
    { key: 'allowedCallersMode', header: 'Quién llama', render: (c) => (c.allowedCallersMode === 'especifico' ? `${(c.allowedCallers || []).length} específicos` : 'cualquiera') },
    { key: 'allowedReceiversMode', header: 'A quién derivar', render: (c) => (c.allowedReceiversMode === 'especifico' ? `${(c.allowedReceivers || []).length} específicos` : 'cualquiera') },
    { key: 'defaultContact', header: 'Contacto por defecto', render: (c) => (c.defaultContact ? c.defaultContact.contactName : '—') },
    { key: 'active', header: 'Estado', render: (c) => (
      <span className={`badge ${c.active ? 'badge-success' : 'badge-gray'}`}>{c.active ? 'activo' : 'inactivo'}</span>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (c) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => openEdit(c)} title="Editar"><Edit2 size={16} /></button>
        <button className="btn-icon" onClick={() => setCaseToDelete(c.id)} title="Borrar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Casos de llamada</h1>
          <p className="page-subtitle">{cases.length} casos definidos</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <Plus size={18} /> Nuevo caso
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input className="form-input" placeholder="Buscar por clave o título..." value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <PhoneCall size={64} />
            <h3>No hay casos definidos</h3>
            <p>Crea un caso para que Minerva sepa cómo responder según el motivo de la llamada.</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map((c) => (
              <div className="item-card" key={c.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar">{c.title.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title">{c.title}</div>
                    <div className="item-card-subtitle" style={{ fontFamily: 'monospace' }}>{c.caseKey}</div>
                  </div>
                  {!fl.isHidden('active') && (
                    <span className={`badge ${c.active ? 'badge-success' : 'badge-gray'}`}>
                      {c.active ? 'activo' : 'inactivo'}
                    </span>
                  )}
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('allowTransfer') && (
                    <div className="item-card-row" style={fl.cardStyle('allowTransfer')}>
                      <span className="item-label">Transferencia</span>
                      <span>{c.allowTransfer ? 'permitida' : 'no'}</span>
                    </div>
                  )}
                  {!fl.isHidden('allowedCallersMode') && (
                    <div className="item-card-row" style={fl.cardStyle('allowedCallersMode')}>
                      <span className="item-label">Quién llama</span>
                      <span>{c.allowedCallersMode === 'especifico' ? `${(c.allowedCallers || []).length} específicos` : 'cualquiera'}</span>
                    </div>
                  )}
                  {!fl.isHidden('allowedReceiversMode') && (
                    <div className="item-card-row" style={fl.cardStyle('allowedReceiversMode')}>
                      <span className="item-label">A quién derivar</span>
                      <span>{c.allowedReceiversMode === 'especifico' ? `${(c.allowedReceivers || []).length} específicos` : 'cualquiera'}</span>
                    </div>
                  )}
                  {!fl.isHidden('defaultContact') && c.defaultContact && (
                    <div className="item-card-row" style={fl.cardStyle('defaultContact')}>
                      <span className="item-label">Contacto por defecto</span>
                      <span>{c.defaultContact.contactName}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn-icon" onClick={() => openEdit(c)} title="Editar"><Edit2 size={16} /></button>
                    <button className="btn-icon" onClick={() => setCaseToDelete(c.id)} title="Borrar" style={{ color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>{editing ? 'Editar caso de llamada' : 'Nuevo caso de llamada'}</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Clave técnica (case_key) *</label>
                    <input className="form-input" value={form.caseKey} required
                      onChange={(e) => setForm({ ...form, caseKey: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Título *</label>
                    <input className="form-input" value={form.title} required
                      onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Instrucciones para Minerva</label>
                  <textarea className="form-input" rows={4} value={form.instructions}
                    onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tipo llamante</label>
                    <select className="form-input" value={form.callerType}
                      onChange={(e) => setForm({ ...form, callerType: e.target.value })}>
                      <option value="cualquiera">Cualquiera</option>
                      <option value="cliente">Cliente</option>
                      <option value="proveedor">Proveedor</option>
                      <option value="propietario">Propietario</option>
                      <option value="contacto">Contacto</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo intención</label>
                    <select className="form-input" value={form.intentType}
                      onChange={(e) => setForm({ ...form, intentType: e.target.value })}>
                      <option value="consulta">Consulta</option>
                      <option value="recado">Recado</option>
                      <option value="transferencia">Transferencia</option>
                      <option value="orden">Orden</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.active}
                        onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                      Activo
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.allowTransfer}
                        onChange={(e) => setForm({ ...form, allowTransfer: e.target.checked })} />
                      Permitir transferencia
                    </label>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.allowMessageEmail}
                        onChange={(e) => setForm({ ...form, allowMessageEmail: e.target.checked })} />
                      Permitir mensaje por email
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.isFallback}
                        onChange={(e) => setForm({ ...form, isFallback: e.target.checked })} />
                      Fallback (último recurso)
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Contacto por defecto al transferir</label>
                  <select className="form-input"
                    value={form.defaultContactId || ''}
                    onChange={(e) => setForm({ ...form, defaultContactId: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">— Ninguno —</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.contactName}{c.phoneNumber ? ` (${c.phoneNumber})` : ''}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quién puede iniciar este caso</label>
                    <select className="form-input" value={form.allowedCallersMode}
                      onChange={(e) => setForm({ ...form, allowedCallersMode: e.target.value })}>
                      <option value="cualquiera">Cualquiera</option>
                      <option value="especifico">Solo contactos específicos</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">A quién derivar</label>
                    <select className="form-input" value={form.allowedReceiversMode}
                      onChange={(e) => setForm({ ...form, allowedReceiversMode: e.target.value })}>
                      <option value="cualquiera">Cualquiera</option>
                      <option value="especifico">Solo contactos específicos</option>
                    </select>
                  </div>
                </div>

                {form.allowedCallersMode === 'especifico' && (
                  <div className="form-group">
                    <label className="form-label">Llamantes permitidos</label>
                    <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {contacts.map((c) => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4 }}>
                          <input type="checkbox" checked={allowedCallerIds.includes(c.id)}
                            onChange={() => toggleId(allowedCallerIds, setAllowedCallerIds, c.id)} />
                          <span>{c.contactName}{c.phoneNumber ? ` · ${c.phoneNumber}` : ''}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.allowedReceiversMode === 'especifico' && (
                  <div className="form-group">
                    <label className="form-label">Destinatarios de transferencia permitidos</label>
                    <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {contacts.map((c) => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4 }}>
                          <input type="checkbox" checked={allowedReceiverIds.includes(c.id)}
                            onChange={() => toggleId(allowedReceiverIds, setAllowedReceiverIds, c.id)} />
                          <span>{c.contactName}{c.phoneNumber ? ` · ${c.phoneNumber}` : ''}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {editing && (
                  <OwnerSelect
                    value={form.ownerId}
                    onChange={(id) => setForm({ ...form, ownerId: id })}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar cambios' : 'Crear caso'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {caseToDelete !== null && (
        <div className="modal-overlay" onClick={() => setCaseToDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar borrado</h2>
              <button className="btn-icon" onClick={() => setCaseToDelete(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>¿Borrar este caso de llamada? Sus enlaces a contactos también desaparecerán.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCaseToDelete(null)}>Cancelar</button>
              <button className="btn btn-danger" onClick={() => handleDelete(caseToDelete)}>Borrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallCases;
